import React, { useEffect, useState, useRef } from "react";
import { apiGet, apiPut, apiPost } from "../../utils/api";
import { Spinner, Badge } from "../../components/ui";
import { FaUndo, FaPaperPlane, FaSave } from "react-icons/fa";
import { useModal } from "../../context/ModalContext";

const CATEGORY_ORDER = ["Auth", "Subscription", "Trial", "Team", "WhatsApp"];

export default function ManageEmailTemplates() {
  const modal = useModal();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const previewTimer = useRef(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await apiGet("/api/admin/email-templates");
      const data = res?.data || [];
      setTemplates(data);
      if (data.length) selectTemplate(data[0]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const selectTemplate = (tpl) => {
    setSelected(tpl);
    setForm({
      subject: tpl.subject,
      heading: tpl.heading,
      body: tpl.body,
      buttonText: tpl.buttonText,
      buttonUrl: tpl.buttonUrl,
      footerNote: tpl.footerNote,
      enabled: tpl.enabled,
    });
    setPreviewHtml("");
    fetchPreview(tpl.eventKey);
  };

  const fetchPreview = async (eventKey) => {
    try {
      const res = await apiPost(`/api/admin/email-templates/${eventKey}/preview?previewOnly=true`);
      if (res?.html) setPreviewHtml(res.html);
    } catch (err) {
      console.error("Preview error:", err);
    }
  };

  // Debounced preview on form change (does NOT auto-save)
  const handleFieldChange = (field, value) => {
    const updated = { ...form, [field]: value };
    setForm(updated);

    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      if (!selected) return;
      try {
        const res = await apiPost(
          `/api/admin/email-templates/${selected.eventKey}/preview?previewOnly=true`,
          updated
        );
        if (res?.html) setPreviewHtml(res.html);
      } catch (err) {
        // silent — preview is best-effort
      }
    }, 800);
  };

  const handleSave = async () => {
    if (!selected) return;
    try {
      setSaving(true);
      const res = await apiPut(`/api/admin/email-templates/${selected.eventKey}`, form);
      if (res?.success) {
        modal.alert("Template saved!", "success");
        setTemplates((prev) =>
          prev.map((t) => (t.eventKey === selected.eventKey ? { ...t, ...form } : t))
        );
        setSelected((prev) => ({ ...prev, ...form }));
        fetchPreview(selected.eventKey);
      }
    } catch (err) {
      modal.alert(err?.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!selected) return;
    try {
      setPreviewing(true);
      await apiPut(`/api/admin/email-templates/${selected.eventKey}`, form);
      const res = await apiPost(`/api/admin/email-templates/${selected.eventKey}/preview`);
      if (res?.success) modal.alert(res.message, "success");
      else modal.alert(res?.message || "Failed to send test", "error");
    } catch (err) {
      modal.alert(err?.message || "Failed to send test email", "error");
    } finally {
      setPreviewing(false);
    }
  };

  const handleReset = async () => {
    if (!selected) return;
    const confirmed = await modal.confirm(
      "Reset this template to its default content? Your customizations will be lost.",
      { danger: true, confirmText: "Reset" }
    );
    if (!confirmed) return;
    try {
      const res = await apiPost(`/api/admin/email-templates/${selected.eventKey}/reset`);
      if (res?.success && res.data) {
        selectTemplate(res.data);
        setTemplates((prev) =>
          prev.map((t) => (t.eventKey === selected.eventKey ? res.data : t))
        );
        modal.alert("Template reset to default", "success");
      }
    } catch (err) {
      modal.alert(err?.message || "Failed to reset", "error");
    }
  };

  const insertVariable = (field, variable) => {
    const tag = `{{${variable}}}`;
    handleFieldChange(field, (form[field] || "") + tag);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-ink-muted">
        <Spinner size="sm" /> Loading templates...
      </div>
    );
  }

  // Group by category
  const grouped = {};
  for (const cat of CATEGORY_ORDER) grouped[cat] = [];
  for (const tpl of templates) {
    const cat = tpl.category || "General";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(tpl);
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-ink-primary mb-5">Email Templates</h2>
      <div className="flex gap-6">
        {/* Left: template list */}
        <div className="w-64 flex-shrink-0">
          {CATEGORY_ORDER.map((cat) =>
            grouped[cat]?.length ? (
              <div key={cat} className="mb-4">
                <p className="text-xs font-semibold text-ink-muted uppercase tracking-wider mb-2 px-2">
                  {cat}
                </p>
                <div className="space-y-1">
                  {grouped[cat].map((tpl) => (
                    <button
                      key={tpl.eventKey}
                      onClick={() => selectTemplate(tpl)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between transition-colors ${
                        selected?.eventKey === tpl.eventKey
                          ? "bg-brand-50 text-brand-700 font-medium"
                          : "text-ink-secondary hover:bg-surface-bg"
                      }`}
                    >
                      <span className="truncate">{tpl.label}</span>
                      <Badge variant={tpl.enabled ? "success" : "danger"} className="ml-2 text-[10px]">
                        {tpl.enabled ? "On" : "Off"}
                      </Badge>
                    </button>
                  ))}
                </div>
              </div>
            ) : null
          )}
        </div>

        {/* Right: editor + preview */}
        {selected ? (
          <div className="flex-1 min-w-0">
            {/* Enabled toggle */}
            <div className="flex items-center justify-between mb-4 p-4 rounded-lg border border-surface-border">
              <div>
                <h3 className="font-semibold text-ink-primary">{selected.label}</h3>
                <p className="text-xs text-ink-muted">{selected.eventKey}</p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="text-sm text-ink-secondary">
                  {form.enabled ? "Enabled" : "Disabled"}
                </span>
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => handleFieldChange("enabled", e.target.checked)}
                  className="toggle"
                />
              </label>
            </div>

            {/* Form fields */}
            <div className="space-y-4 mb-6">
              <TemplateField
                label="Subject"
                value={form.subject}
                onChange={(v) => handleFieldChange("subject", v)}
                variables={selected.variables}
                onInsertVar={(v) => insertVariable("subject", v)}
              />
              <TemplateField
                label="Heading"
                value={form.heading}
                onChange={(v) => handleFieldChange("heading", v)}
                variables={selected.variables}
                onInsertVar={(v) => insertVariable("heading", v)}
              />
              <TemplateField
                label="Body"
                value={form.body}
                onChange={(v) => handleFieldChange("body", v)}
                variables={selected.variables}
                onInsertVar={(v) => insertVariable("body", v)}
                multiline
              />
              <div className="grid grid-cols-2 gap-4">
                <TemplateField
                  label="Button Text"
                  value={form.buttonText}
                  onChange={(v) => handleFieldChange("buttonText", v)}
                  placeholder="Leave empty for no button"
                />
                <TemplateField
                  label="Button URL"
                  value={form.buttonUrl}
                  onChange={(v) => handleFieldChange("buttonUrl", v)}
                  variables={selected.variables}
                  onInsertVar={(v) => insertVariable("buttonUrl", v)}
                />
              </div>
              <TemplateField
                label="Footer Note"
                value={form.footerNote}
                onChange={(v) => handleFieldChange("footerNote", v)}
                placeholder="Optional extra line above footer"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 mb-6">
              <button className="btn-primary flex items-center gap-2" onClick={handleSave} disabled={saving}>
                {saving ? <Spinner size="sm" /> : <FaSave size={12} />} Save
              </button>
              <button className="btn-secondary flex items-center gap-2" onClick={handleSendTest} disabled={previewing}>
                {previewing ? <Spinner size="sm" /> : <FaPaperPlane size={12} />} Send Test Email
              </button>
              <button className="btn-danger flex items-center gap-2" onClick={handleReset}>
                <FaUndo size={12} /> Reset to Default
              </button>
            </div>

            {/* Live Preview */}
            {previewHtml && (
              <div>
                <h4 className="text-sm font-semibold text-ink-primary mb-2">Preview</h4>
                <div className="border border-surface-border rounded-lg overflow-hidden bg-white">
                  <iframe
                    srcDoc={previewHtml}
                    title="Email Preview"
                    className="w-full border-0"
                    style={{ minHeight: 500 }}
                    sandbox=""
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-ink-muted">
            Select a template to edit
          </div>
        )}
      </div>
    </div>
  );
}

function TemplateField({ label, value, onChange, variables, onInsertVar, multiline, placeholder }) {
  return (
    <div>
      <label className="block text-sm font-medium text-ink-secondary mb-1">{label}</label>
      {multiline ? (
        <textarea
          className="input w-full"
          rows={6}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      ) : (
        <input
          type="text"
          className="input w-full"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      )}
      {variables?.length > 0 && onInsertVar && (
        <div className="flex gap-1.5 mt-1.5 flex-wrap">
          {variables.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => onInsertVar(v)}
              className="px-2 py-0.5 text-[11px] rounded-full bg-brand-50 text-brand-700 hover:bg-brand-100 transition-colors"
            >
              {`{{${v}}}`}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
